import { ErrorWithBreadcrumb } from '~/errors';

export const useSiteConfigurationStore = defineStore('SiteConfiguration', () => {
  const configurationAPI = useConfigurationAPI();

  const siteConfiguration = ref<SiteConfiguration>();

  const config = computed(() => {
    if (siteConfiguration.value === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.SiteConfiguration, message: 'Site configuration is not set' });
    }

    return readonly(siteConfiguration.value);
  });

  const allUnits = computed(() => {
    if (units.value === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.SiteConfiguration, message: 'Units are not set' });
    }

    return readonly(units.value);
  });

  async function setSiteConfiguration() {
    const response = await configurationAPI.getSiteConfiguration();

    if (response !== undefined && !response.HasErrors) {
      siteConfiguration.value = response.Data;
    }
  }

  function WorksheetStatusToWorkflowStatus(status: WorksheetStatus): WorkflowStatus {
    switch (status) {
      case WorksheetStatus.Closed:
        return WorkflowStatus.Closed;
  
      case WorksheetStatus.InProgress:
        return WorkflowStatus.InProgress;
  
      case WorksheetStatus.NotStarted:
        return WorkflowStatus.NotStarted;
  
      case WorksheetStatus.Completed:
        return WorkflowStatus.Completed;
  
      case WorksheetStatus.Exported:
        return WorkflowStatus.Completed;
  
      case WorksheetStatus.PartiallyCompleted:
        return WorkflowStatus.PartiallyCompleted;
  
      default:
        return WorkflowStatus.NotDefined;
    }
  }

  const units = ref<Unit[]>();

  async function setUnits() {
    const response = await configurationAPI.getUnits();

    if (response !== undefined && response.HasErrors === false) {
      units.value = response.Data;
    }
  }

  const { getBankHolidays } = useConfigurationAPI();
  const bankHolidays = ref<BankHoliday[]>([]);

  async function loadBankHolidays() {
    const response = await getBankHolidays();

    if (response?.HasErrors === false) {
      bankHolidays.value = response.Data;
    }
  }

  const holidayHighlights = computed<DateHighlight[]>(() => {
    return bankHolidays.value.map((holiday) => {
      return {
        date: holiday.Date.split('T')[0],
        name: holiday.Name,
        backgroundColor: 'var(--ion-color-step-150)'
      };
    });
  });

  const holidayHighlightsDictionary = computed(() => {
    const holidayDictionary: Record<string, DateHighlight[]> = {};

    for (const holiday of holidayHighlights.value) {
      holidayDictionary[holiday.date] ??= [];
      holidayDictionary[holiday.date].push(holiday);
    }

    return holidayDictionary;
  });

  function $reset(): void {
    bankHolidays.value = [];
    siteConfiguration.value = {
      CompanyInfo: {
        DepartmentID: 0,
        ShortName: '',
        Name: '',
        Logo: '',
        CompanyName: '',
        City: '',
        Address: '',
        Zip: '',
        Country: '',
        Phone: '',
        DirectPhone: '',
        CVRNumber: '',
        Email: ''
      },
      StatusDefaults: [],
      OfficePhoneNumber: ''
    };
  }

  return {
    _bankHolidays: bankHolidays,
    bankHolidays: readonly(bankHolidays),
    _siteConfiguration: siteConfiguration,
    siteConfiguration: readonly(siteConfiguration),
    _units: units,
    units: allUnits,
    holidayHighlights,
    holidayHighlightsDictionary,
    setBankHolidays: loadBankHolidays,
    setSiteConfiguration,
    setUnits,
    WorksheetStatusToWorkflowStatus,
    config,
    $reset
  };
});
